import React from "react";
import { useSite } from "../../context/SiteContext";
import { Sparkles, DollarSign, Clapperboard, ShieldCheck, Award } from "lucide-react";

const iconFor = (key) => {
  if (key.startsWith("AI")) return <Sparkles size={18} />;
  if (key.startsWith("Monet")) return <DollarSign size={18} />;
  return <Clapperboard size={18} />;
};

const Skills = () => {
  const { skills, aiTools, certifications, aiActTimeline } = useSite();
  const entries = Object.entries(skills || {});

  return (
    <section
      id="skills"
      className="relative bg-[#0a0a0a] py-24 md:py-36 border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-14">
          <div className="md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300/90 mb-4">
              04 — Capabilities
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-stone-50 leading-tight">
              The <span className="italic">toolkit.</span>
            </h2>
          </div>
          <p className="md:col-span-7 md:col-start-6 text-stone-400 text-[16px] md:text-lg leading-[1.7]">
            A working studio — generative AI, fine-tuned LLMs, EU-AI-Act-aware
            governance, plus the creative and operational craft to actually ship.
          </p>
        </div>

        {/* Main 3-column grouped skills */}
        <div className="grid md:grid-cols-3 gap-px bg-zinc-900">
          {entries.map(([group, items]) => (
            <div
              key={group}
              className="bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#0f0f0f] transition-colors"
            >
              <div className="flex items-center gap-3 mb-6 text-amber-300">
                {iconFor(group)}
                <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                  {group}
                </span>
              </div>
              <ul className="space-y-3">
                {items.map((it) => (
                  <li
                    key={it}
                    className="font-display text-xl md:text-2xl text-stone-100 leading-snug"
                  >
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* C2 — AI Tooling tiles */}
        <div className="mt-16 md:mt-24">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-500 mb-6">
            AI tooling — day-to-day
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
            {(aiTools || []).map((t) => (
              <div
                key={t.name}
                className="group flex items-center gap-3 p-4 border border-zinc-800 hover:border-amber-300/40 transition-colors"
              >
                <span
                  className={`w-10 h-10 shrink-0 rounded-sm border flex items-center justify-center font-display text-xl ${t.color}`}
                >
                  {t.letter}
                </span>
                <span className="text-stone-200 text-sm leading-tight">{t.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* C1 — EU AI Act compliance badge + timeline */}
        <div className="mt-16 md:mt-24 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-5 border border-amber-400/25 bg-amber-400/[0.04] p-8">
            <div className="flex items-center gap-3 mb-4 text-amber-300">
              <ShieldCheck size={18} />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                EU AI Act — compliance-ready
              </span>
            </div>
            <p className="font-display text-2xl md:text-3xl text-stone-50 leading-snug">
              Author of a <span className="italic">12-module compliance course</span> for
              AI &amp; Partners — covering onboarding, risk classification and audit.
            </p>
            <p className="mt-4 text-stone-400 text-sm leading-relaxed">
              I help content-heavy businesses map their AI stack against the Act's
              obligations — from prohibited-practice screening through high-risk system
              readiness.
            </p>
          </div>

          <div className="md:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-500 mb-6">
              Key milestones I actively advise on
            </p>
            <ol className="relative border-l border-zinc-800 ml-2 space-y-6">
              {(aiActTimeline || []).map((m) => (
                <li key={m.date} className="pl-6 relative">
                  <span className="absolute -left-[5px] top-2 w-2.5 h-2.5 rounded-full bg-amber-300 ring-4 ring-[#0a0a0a]" />
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-300/80">
                    {m.date}
                  </p>
                  <p className="mt-1 font-display text-xl text-stone-100">
                    {m.label}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* C3 — Certifications / credentials row */}
        <div className="mt-16 md:mt-24 border-t border-zinc-900 pt-10">
          <div className="flex items-center gap-3 mb-6 text-stone-500">
            <Award size={16} />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em]">
              Credentials
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900">
            {(certifications || []).map((c) => (
              <div key={c.title} className="bg-[#0a0a0a] p-6 hover:bg-[#0f0f0f] transition-colors">
                <p className="font-display text-lg md:text-xl text-stone-50 leading-snug">
                  {c.title}
                </p>
                <p className="mt-2 text-stone-500 text-sm leading-relaxed">{c.org}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
